import clsx from 'clsx';

import { Highlight } from '../Highlight';
import { parseStructured } from '../parse/structuredParse';
import { CellRenderer, CellRenderProps, CellValue } from '../types';

import styles from './conversation.module.scss';

interface Turn {
  role: string;
  content: string;
}

const isTurn = (item: unknown): item is Turn =>
  typeof item === 'object' &&
  item !== null &&
  'role' in item &&
  'content' in item &&
  typeof (item as Record<string, unknown>).role === 'string';

// Returns the turn array when the value parses to an array of {role, content}, else null.
const toTurns = (value: CellValue): Turn[] | null => {
  const parsed = parseStructured(value);
  if (!parsed.ok) return null;
  const { data } = parsed;
  if (!Array.isArray(data) || data.length === 0) return null;
  if (!data.every(isTurn)) return null;
  return data.map(item => ({
    role: String((item as Turn).role),
    content:
      typeof (item as Turn).content === 'string' ? (item as Turn).content : JSON.stringify((item as Turn).content),
  }));
};

const detect = (samples: CellValue[]): boolean => samples.length > 0 && samples.every(v => toTurns(v) !== null);

const isAssistant = (role: string) => /assist|bot|system|ai|model/i.test(role);

const Bubble = ({ turn }: { turn: Turn }) => (
  <div className={clsx(styles.bubble, isAssistant(turn.role) ? styles.assistant : styles.user)}>
    <div className={styles.who}>{turn.role}</div>
    <Highlight text={turn.content} />
  </div>
);

const Compact = ({ value }: CellRenderProps) => {
  const turns = toTurns(value);
  if (!turns) return <span className={styles.compact}>{String(value)}</span>;

  const firstUser = turns.find(t => !isAssistant(t.role)) ?? turns[0];
  const roleFlow = `${turns[0].role} → ${turns[turns.length - 1].role}`;

  return (
    <div className={styles.compactWrap}>
      <div className={styles.compactTop}>
        <span className={styles.kind}>{turns.length} turns</span>
        <span className={styles.flow}>{roleFlow}</span>
      </div>
      <div className={styles.peek}>
        <Highlight text={firstUser.content} />
      </div>
    </div>
  );
};

const Conversation = ({ value, narrow }: CellRenderProps & { narrow?: boolean }) => {
  const turns = toTurns(value);
  if (!turns) return <span>{String(value)}</span>;
  return (
    <div className={clsx(styles.conv, narrow && styles.narrow)}>
      {turns.map((turn, i) => (
        <Bubble
          // Read-only preview: turns never reorder, so index is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          turn={turn}
        />
      ))}
    </div>
  );
};

const Expanded = ({ value }: CellRenderProps) => (
  <Conversation
    value={value}
    narrow
  />
);

const Full = ({ value }: CellRenderProps) => <Conversation value={value} />;

export const conversationRenderer: CellRenderer = {
  kind: 'conversation',
  detect,
  Compact,
  Expanded,
  Full,
};
